import { useMutation } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { AxiosWrapper } from 'services/AxiosWrapper';
import { ItineraryManagerApiService } from 'services/ItineraryManagerApi';
import { useNotifications } from 'hooks/useNotifications';

export const useVoucherPdfExport = (
  itineraryManagerApi: ItineraryManagerApiService<AxiosWrapper<AxiosInstance>>,
  voucherUuid: string | undefined,
  accessUuid: string | null | undefined
) => {
  const notifications = useNotifications();

  const generatePdf = useMutation({
    mutationFn: async () => {
      if (!voucherUuid) {
        throw new Error('Voucher UUID is required');
      }
      return itineraryManagerApi.generatePdf(voucherUuid, accessUuid);
    },
    onSuccess: response => {
      const pdfUrl = response.data?.url;
      if (pdfUrl) {
        window.open(pdfUrl, '_blank');
        notifications.showSuccessNotification('PDF exported successfully');
      } else {
        notifications.showErrorNotification('Failed to generate PDF. Please try again.');
      }
    },
    onError: error => {
      console.error('Error generating PDF:', error);
      notifications.showErrorNotification('Failed to generate PDF. Please try again.');
    },
  });

  const generatePdfAsLoggedIn = useMutation({
    mutationFn: async () => {
      if (!voucherUuid) {
        throw new Error('Voucher UUID is required');
      }
      
      // First, get a fresh accessUuid by calling checkIsOtpRequired
      const otpResponse = await itineraryManagerApi.checkIsOtpRequired(voucherUuid, null);
      const freshAccessUuid = otpResponse.data?.accessUuid;
      
      if (!freshAccessUuid) {
        throw new Error('Failed to get access UUID');
      }
      
      // Then use the fresh accessUuid to generate the PDF
      return itineraryManagerApi.generatePdf(voucherUuid, freshAccessUuid);
    },
    onSuccess: response => {
      const pdfUrl = response.data?.url;
      if (pdfUrl) {
        window.open(pdfUrl, '_blank');
        notifications.showSuccessNotification('PDF exported successfully');
      } else {
        notifications.showErrorNotification('Failed to generate PDF. Please try again.');
      }
    },
    onError: error => {
      console.error('Error generating PDF:', error);
      notifications.showErrorNotification('Failed to generate PDF. Please try again.');
    },
  });

  return {
    generatePdf,
    generatePdfAsLoggedIn,
  };
};
